/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useCallback } from 'react';
import type { PlanCell, AllowedBoundingBox, StructuralObject } from '../../types/plan';
import type { PlotGridLayers } from '../../types/bulkOperations';
import type { EntityInteractionState } from '../../hooks/useEntityInteraction';
import type { EntityLookup } from '../../utils/planEntities';
import PlanGridRow from './planGrid/PlanGridRow';
import { useContourBorders } from './planGrid/useContourBorders';
import { useGreenhouseGroups, computeGreenhouseContour } from './planGrid/useGreenhouseGroups';
import { planCellKey } from '../../utils/planCells';
import type { GridSelectionRect, BulkPlacementPreview } from './planEditor/types';
import type { PlanAppMode, PlanLayerVisibility } from '../../types/planManagement';

type AppMode = PlanAppMode;
type EditorMode = 'navigate' | 'paint' | 'erase' | 'bed' | 'plot' | 'delete-bed' | 'delete-plot';
type GreenhouseCrossbarOrientation = 'vertical' | 'horizontal';

interface PlanGridProps {
  allowedBoundingBox: AllowedBoundingBox;
  cellMap: Map<string, PlanCell>;
  structuralObjects?: StructuralObject[];
  isSelectedCell: (row: number, col: number) => boolean;
  highlightCells?: Set<string>;
  selectionRect: GridSelectionRect | null;
  entityState: EntityInteractionState;
  entityLookup: EntityLookup;
  multiSelectedBeds: Set<string>;  // CN-0012: UUID-only
  multiSelectedPlots: Set<string>;  // CN-0012: UUID-only
  mode: EditorMode;
  appMode: AppMode;
  /** CN-0015 UX: Whether plot-level planning with selection is enabled */
  allowBedSelection?: boolean;
  isTransplantTargetMode?: boolean;
  /** CN-0015 UX: Cells highlighted as finalized planning selection */
  planningSelectedCells?: Set<string>;
  bulkPlacementPreview: BulkPlacementPreview | null;
  /** CN-0015: Bed IDs that have active plantings — show green dot indicator */
  bedsWithPlantings?: Set<string>;
  layerData?: PlotGridLayers;
  layerVisibility?: PlanLayerVisibility;
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent<HTMLDivElement>) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onCellMouseLeave: () => void;
  onCellMouseUp: () => void;
}

/**
 * Generate array of global coordinates from min to max, skipping 0.
 * Example: generateRange(-2, 3) => [-2, -1, 1, 2, 3]
 */
const generateRange = (min: number, max: number): number[] => {
  const result: number[] = [];
  for (let i = min; i <= max; i += 1) {
    if (i !== 0) result.push(i);
  }
  return result;
};

const pickGeometricCenterCell = (keys: string[]): string => {
  if (keys.length <= 1) return keys[0];

  const coords = keys.map((key) => {
    const [row, col] = key.split(':').map(Number);
    return { key, row, col };
  });

  const avgRow = coords.reduce((sum, item) => sum + item.row, 0) / coords.length;
  const avgCol = coords.reduce((sum, item) => sum + item.col, 0) / coords.length;

  const sorted = [...coords].sort((a, b) => {
    const aDist = (a.row - avgRow) ** 2 + (a.col - avgCol) ** 2;
    const bDist = (b.row - avgRow) ** 2 + (b.col - avgCol) ** 2;
    if (aDist !== bDist) return aDist - bDist;

    const aRowBias = Math.abs(a.row - avgRow);
    const bRowBias = Math.abs(b.row - avgRow);
    if (aRowBias !== bRowBias) return aRowBias - bRowBias;

    const aColBias = Math.abs(a.col - avgCol);
    const bColBias = Math.abs(b.col - avgCol);
    if (aColBias !== bColBias) return aColBias - bColBias;

    if (a.row !== b.row) return b.row - a.row;
    return b.col - a.col;
  });

  return sorted[0].key;
};

const PlanGrid: React.FC<PlanGridProps> = ({
  allowedBoundingBox,
  cellMap,
  structuralObjects = [],
  isSelectedCell,
  highlightCells,
  selectionRect,
  entityState,
  entityLookup,
  multiSelectedBeds,
  multiSelectedPlots,
  mode,
  appMode,
  allowBedSelection = false,
  isTransplantTargetMode = false,
  planningSelectedCells,
  bulkPlacementPreview,
  bedsWithPlantings,
  layerData,
  layerVisibility,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseLeave,
  onCellMouseUp,
}) => {
  // Generate row and column ranges (global coordinates, skipping 0)
  const rows = useMemo(
    () => generateRange(allowedBoundingBox.minRow, allowedBoundingBox.maxRow),
    [allowedBoundingBox.minRow, allowedBoundingBox.maxRow]
  );
  const cols = useMemo(
    () => generateRange(allowedBoundingBox.minCol, allowedBoundingBox.maxCol),
    [allowedBoundingBox.minCol, allowedBoundingBox.maxCol]
  );

  const contourBorders = useContourBorders(entityState.hoveredCellKeys, entityState.selectedCellKeys);

  // Track the raw cell key the cursor is directly over (for greenhouse group hover)
  const [rawHoveredKey, setRawHoveredKey] = useState<string | null>(null);

  const wrappedOnCellMouseEnter = useCallback((row: number, col: number) => {
    setRawHoveredKey(planCellKey(row, col));
    onCellMouseEnter(row, col);
  }, [onCellMouseEnter]);

  const wrappedOnCellMouseLeave = useCallback(() => {
    setRawHoveredKey(null);
    onCellMouseLeave();
  }, [onCellMouseLeave]);

  const greenhouseGroups = useGreenhouseGroups(cellMap, structuralObjects);

  const structuralObjectByCell = useMemo(() => {
    const byId = new Map(structuralObjects.map((item) => [item.id, item]));
    const byCell = new Map<string, StructuralObject>();
    cellMap.forEach((cell, key) => {
      if (!cell.structuralObjectId) return;
      const structuralObject = byId.get(cell.structuralObjectId);
      if (structuralObject) byCell.set(key, structuralObject);
    });
    return byCell;
  }, [cellMap, structuralObjects]);

  // Pre-compute: greenhouse contour borders + cell→group membership
  const [ghContourBorders, ghCellGroupMap, ghCrossbars] = useMemo(() => {
    const borders = new Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>();
    const cellGroup = new Map<string, string>(); // cellKey → groupId
    const crossbars = new Map<string, GreenhouseCrossbarOrientation>(); // cellKey → crossbar direction

    for (const group of greenhouseGroups) {
      const contour = computeGreenhouseContour(group.cellKeys);
      contour.forEach((b, key) => {
        borders.set(key, b);
        cellGroup.set(key, group.id);
      });

      // Crossbars: perpendicular to the long side, every other cell along it
      const rowSpan = group.maxRow - group.minRow + (group.minRow <= 0 && group.maxRow >= 0 ? 0 : 1);
      const colSpan = group.maxCol - group.minCol + (group.minCol <= 0 && group.maxCol >= 0 ? 0 : 1);
      const isHorizontal = colSpan >= rowSpan; // long side is horizontal → crossbars run vertically

      group.cellKeys.forEach((key) => {
        const [rStr, cStr] = key.split(':');
        const coord = isHorizontal ? parseInt(cStr, 10) : parseInt(rStr, 10);
        // Every other coord along the long axis starting from second
        if (Math.abs(coord) % 2 === 0) {
          crossbars.set(key, isHorizontal ? 'vertical' : 'horizontal');
        }
      });
    }

    return [borders, cellGroup, crossbars] as const;
  }, [greenhouseGroups]);

  // Determine if any selected entity (bed) is inside a greenhouse
  const activeGreenhouseGroups = useMemo(() => {
    const active = new Set<string>();
    entityState.selectedCellKeys.forEach((key) => {
      const gId = ghCellGroupMap.get(key);
      if (gId) active.add(gId);
    });
    return active;
  }, [entityState.selectedCellKeys, ghCellGroupMap]);

  // Determine if any greenhouse group is currently hovered (cursor over any cell in the group)
  const hoveredGreenhouseGroups = useMemo(() => {
    const hovered = new Set<string>();
    if (rawHoveredKey) {
      const gId = ghCellGroupMap.get(rawHoveredKey);
      if (gId) hovered.add(gId);
    }
    // Also cover multi-cell bed hover (all cells of the hovered entity)
    const hoveredKeys = Array.from(entityState.hoveredCellKeys);
    for (let i = 0; i < hoveredKeys.length; i++) {
      const gId = ghCellGroupMap.get(hoveredKeys[i]);
      if (gId) hovered.add(gId);
    }
    return hovered;
  }, [rawHoveredKey, ghCellGroupMap, entityState.hoveredCellKeys]);

  // Pre-compute bed boundary borders: for each bed cell, which edges are on the outer perimeter
  const bedBoundaryBorders = useMemo(() => {
    // Build bedId per cellKey
    const cellBedId = new Map<string, string>();
    cellMap.forEach((cell, key) => {
      if (cell.kind === 'bed' && cell.bedId) {
        cellBedId.set(key, cell.bedId);
      }
    });

    const borders = new Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>();
    cellBedId.forEach((bedId, key) => {
      const [rowStr, colStr] = key.split(':');
      const row = parseInt(rowStr, 10);
      const col = parseInt(colStr, 10);

      const topRow = row === 1 ? -1 : row - 1;
      const bottomRow = row === -1 ? 1 : row + 1;
      const leftCol = col === 1 ? -1 : col - 1;
      const rightCol = col === -1 ? 1 : col + 1;

      borders.set(key, {
        top: cellBedId.get(planCellKey(topRow, col)) !== bedId,
        bottom: cellBedId.get(planCellKey(bottomRow, col)) !== bedId,
        left: cellBedId.get(planCellKey(row, leftCol)) !== bedId,
        right: cellBedId.get(planCellKey(row, rightCol)) !== bedId,
      });
    });
    return borders;
  }, [cellMap]);

  // Pick one center cell per bed for planting indicator placement
  const bedPlantingCells = useMemo(() => {
    if (layerVisibility?.plantings === false) return new Set<string>();
    if (!bedsWithPlantings || bedsWithPlantings.size === 0) return new Set<string>();

    const bedCellKeys = new Map<string, string[]>();
    cellMap.forEach((cell, key) => {
      if (cell.kind === 'bed' && cell.bedId && bedsWithPlantings.has(cell.bedId)) {
        const arr = bedCellKeys.get(cell.bedId) || [];
        arr.push(key);
        bedCellKeys.set(cell.bedId, arr);
      }
    });

    const centers = new Set<string>();
    bedCellKeys.forEach((keys) => {
      centers.add(pickGeometricCenterCell(keys));
    });
    return centers;
  }, [cellMap, bedsWithPlantings, layerVisibility?.plantings]);

  // Pick one center cell per bed for watering indicator placement
  const bedWateringCells = useMemo(() => {
    const wateringByBed = layerData?.watering?.byBed;
    if (!wateringByBed) return new Set<string>();

    const bedIds = new Set(Object.keys(wateringByBed));
    if (bedIds.size === 0) return new Set<string>();

    const bedCellKeys = new Map<string, string[]>();
    cellMap.forEach((cell, key) => {
      if (cell.kind === 'bed' && cell.bedId && bedIds.has(cell.bedId)) {
        const arr = bedCellKeys.get(cell.bedId) || [];
        arr.push(key);
        bedCellKeys.set(cell.bedId, arr);
      }
    });

    const centers = new Set<string>();
    bedCellKeys.forEach((keys) => {
      centers.add(pickGeometricCenterCell(keys));
    });
    return centers;
  }, [cellMap, layerData?.watering?.byBed]);

  return (
    <>
      {rows.map((rowGlobal) => (
        <PlanGridRow
          key={`row-${rowGlobal}`}
          rowGlobal={rowGlobal}
          cols={cols}
          cellMap={cellMap}
          structuralObjectByCell={structuralObjectByCell}
          isSelectedCell={isSelectedCell}
          highlightCells={highlightCells}
          selectionRect={selectionRect}
          entityState={entityState}
          entityLookup={entityLookup}
          multiSelectedBeds={multiSelectedBeds}
          multiSelectedPlots={multiSelectedPlots}
          appMode={appMode}
          allowBedSelection={allowBedSelection}
          isTransplantTargetMode={isTransplantTargetMode}
          mode={mode === 'erase' ? 'erase' : 'navigate'}
          contourBorders={contourBorders}
          ghContourBorders={ghContourBorders}
          ghCellGroupMap={ghCellGroupMap}
          ghCrossbars={ghCrossbars}
          activeGreenhouseGroups={activeGreenhouseGroups}
          hoveredGreenhouseGroups={hoveredGreenhouseGroups}
          planningSelectedCells={planningSelectedCells}
          bulkPlacementPreview={bulkPlacementPreview}
          bedsWithPlantings={bedsWithPlantings}
          bedBoundaryBorders={bedBoundaryBorders}
          bedPlantingCells={bedPlantingCells}
          bedWateringCells={bedWateringCells}
          layerData={layerData}
          layerVisibility={layerVisibility}
          onCellMouseDown={onCellMouseDown}
          onCellMouseEnter={wrappedOnCellMouseEnter}
          onCellMouseLeave={wrappedOnCellMouseLeave}
          onCellMouseUp={onCellMouseUp}
        />
      ))}
    </>
  );
};

export default PlanGrid;
